import type { Company } from '@/scenes/authentication/types/types.auth';
import type { SnackBarConfig } from '../Snackbar';
import type { CompanyOption } from './FollowCompanyModal.types';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import CompanyLogo from '../CompanyLogo';
import CompanySearchAutocomplete from '../CompanySearch';
import CustomSnackbar from '../Snackbar';
import { LogoWrapper } from './FollowCompanyModal.styles';

interface FollowCompanyMobileModalProps {
  selectedCompany: CompanyOption | null;
  setSelectedCompany: (company: CompanyOption | null) => void;
  followedCompanyChips: Company[];
  handleFollowCompany: (company: CompanyOption) => Promise<void>;
  handleUnfollowCompany: (companyId: number) => void;
  snackbarConfig: SnackBarConfig;
  handleSnackBarClose: () => void;
  onAddCompany?: () => void;
}

function FollowCompanyMobileModal(props: FollowCompanyMobileModalProps) {
  const {
    setSelectedCompany,
    selectedCompany,
    followedCompanyChips,
    handleFollowCompany,
    handleUnfollowCompany,
    snackbarConfig,
    handleSnackBarClose,
    onAddCompany,
  } = props;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="body2" mb={2}>
        Select a company to follow. You'll receive email notifications when new reviews or intelligence requests are
        posted for that company.
      </Typography>
      <CompanySearchAutocomplete
        value={selectedCompany}
        onChange={(company) => {
          if (company?.id) {
            handleFollowCompany(company).then(() => {
              setTimeout(() => {
                setSelectedCompany(null);
              }, 1000);
            });
          } else if (company?.label && !company.id) {
            // User typed a new company name, show the add modal
            onAddCompany?.();
          } else {
            setSelectedCompany(company);
          }
        }}
        label="Target Company"
        placeholder="Find a company to follow"
        helperText="Search for a company to follow and get notifications"
        minSearchLength={2}
        allowNewCompany={true}
      />
      {followedCompanyChips.length > 0 && (
        <Typography variant="subtitle2" color="textSecondary" fontWeight="bold" mt={2} mb={1}>
          Followed Companies
        </Typography>
      )}
      <Box mt={3} display="flex" flexDirection="column" flexWrap="wrap" gap={1}>
        {followedCompanyChips.map((company) => (
          <Box
            key={company.id}
            sx={{
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
              backgroundColor: '#f7f7f7',
              border: '1px solid #e0e0e0',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              p: 1,
              mb: 1,
              borderRadius: '8px',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <LogoWrapper small>
              <CompanyLogo
                companyName={company?.companyName ?? ''}
                companyId={company?.id}
                sx={{ width: '100%', height: '100%' }}
                alt={company?.companyName}
              />
            </LogoWrapper>
            <Typography variant="body2" style={{ flexGrow: 1 }}>
              {company.companyName}
            </Typography>
            <IconButton size="small" onClick={() => handleUnfollowCompany(company.id!)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </Box>
  );
}

export default FollowCompanyMobileModal;
